import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

import {
    usePlaybackStore,
    calcTimelineZoomScale,
    attachTimelineScrollbarInteractions,
    getTimelineScrollbarElements,
    updateTimelineScrollbarFull,
    updateTimelineScrollbarPositionOnly,
} from '.';

import { processTransAudioUrl } from '../../../utils/videoUrl';
import {
    formatSecondsToTimestamp,
    formatTimelineTime,
    parseTimeToSeconds,
    preprocessSubtitlesByTargetTime,
} from '../../../utils/time.utils';
import {
    adjustVolumeAndSpeedApi,
    adjustTimestampRangeApi,
    adjustOriginTimestampRangeApi,
} from '../../../service/subtitles';
import { audioManager } from '../../../utils/audioManager';
import {
    clientXToTime,
    sortSegmentsByStart,
    computeOriginTimestampResizeLeft,
    computeOriginTimestampResizeRight,
    type IndexedSegment,
} from './subtitleTimelineResizeMath';

export interface TimeboxControllerParams {
    videoData: Task.GetTaskStatusResult | null;
    isLoadingVideo: boolean;
    duration: number;
    targetText: any[];
    setVideoData: React.Dispatch<React.SetStateAction<Task.GetTaskStatusResult | null>>;
    setTargetText: React.Dispatch<React.SetStateAction<any[]>>;
    onPlayheadMove: (time: number) => void;
    onSwitchTrack: (track: 'translated' | 'original') => void;
    /** 纯净版校对：允许原轨道校准时间后同步左侧原词列表的 timeRange */
    isFromPure?: boolean;
    onOriginTimestampSaved?: (subtitleId: number, originStart: string, originEnd: string) => void;
    // Optional: refs owned by videoShow, passed for real-time speed sync during drag
    transAudioRef?: React.MutableRefObject<HTMLAudioElement | null>;
    currentPlayingSubtitleIdRef?: React.MutableRefObject<number | null>;
    currentPlayingInstanceRef?: React.MutableRefObject<any>;
    segmentPlayingLockLineIdRef?: React.MutableRefObject<number | null>;
}

export const useTimeboxController = ({
    videoData,
    isLoadingVideo,
    duration,
    targetText,
    setVideoData,
    setTargetText,
    onPlayheadMove,
    onSwitchTrack,
    isFromPure = false,
    onOriginTimestampSaved,
    transAudioRef,
    currentPlayingSubtitleIdRef,
    currentPlayingInstanceRef,
    segmentPlayingLockLineIdRef,
}: TimeboxControllerParams) => {
        const [isAdjustingSpeed, setIsAdjustingSpeed] = useState(false); // 倍速调节加载状态
        const [pendingTimelineUpdates, setPendingTimelineUpdates] = useState<Map<number, {
        audio_speed: number;
        post_start_time: string;
        post_end_time: string;
    }>>(new Map());
        const [zoomLevel, setZoomLevel] = useState(20);

        // TimelineEditor实例引用
    const timelineEditorRef = useRef<TimelineEditor | null>(null);

    /** React 声明式翻译轨道：与 TimelineEditor 的选中/激活轨道同步 */
    const [timelineSelectedSubtitleId, setTimelineSelectedSubtitleId] = useState<number | null>(null);
    const [timelineActiveTrack, setTimelineActiveTrack] = useState<'translated' | 'original'>('translated');
    
    // 防抖定时器引用
    const zoomDebounceRef = useRef<NodeJS.Timeout | null>(null);
    // 节流定时器引用
    const zoomThrottleRef = useRef<NodeJS.Timeout | null>(null);
    // 上次缩放值引用，用于避免重复计算
    const lastZoomValueRef = useRef<number>(20);
    
    // 缩放控制处理函数（实时更新UI，延迟执行重计算）
    const handleZoomChange = (value: number) => {
        setZoomLevel(value);
        
        // 节流控制：只有在值变化超过阈值时才执行轻量级更新
        const zoomThreshold = 2; // 缩放值变化阈值
        if (Math.abs(value - lastZoomValueRef.current) >= zoomThreshold) {
            // 清除之前的节流定时器
            if (zoomThrottleRef.current) {
                clearTimeout(zoomThrottleRef.current);
            }
            
            // 设置节流定时器，限制更新频率
            zoomThrottleRef.current = setTimeout(() => {
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.updateZoomLight(value);
                    lastZoomValueRef.current = value;
                }
            }, 16); // 约60fps的更新频率
        }
    };
    
    // 拖动结束后的处理函数
    const handleZoomAfterChange = (value: number) => {
        // 清除之前的防抖定时器
        if (zoomDebounceRef.current) {
            clearTimeout(zoomDebounceRef.current);
        }
        
        // 清除节流定时器
        if (zoomThrottleRef.current) {
            clearTimeout(zoomThrottleRef.current);
        }
        
        // 设置防抖定时器，延迟执行完整的缩放操作
        zoomDebounceRef.current = setTimeout(() => {
            if (timelineEditorRef.current) {
                timelineEditorRef.current.updateZoomComplete(value);
                lastZoomValueRef.current = value;
            }
        }, 200); // 减少防抖延迟到200ms
    };
    
    const handleZoomIn = () => {
        const newValue = Math.min(100, zoomLevel + 10);
        setZoomLevel(newValue);
        if (timelineEditorRef.current) {
            timelineEditorRef.current.updateZoom(newValue);
        }
    };
    
    const handleZoomOut = () => {
        const newValue = Math.max(0, zoomLevel - 10);
        setZoomLevel(newValue);
        if (timelineEditorRef.current) {
            timelineEditorRef.current.updateZoom(newValue);
        }
    };

        const isPlayingRef = useRef(false);
    const currentTimeRef = useRef(0);
    const playbackManagerRef = useRef<any>(null);
    /** PlayheadController 每帧读取，避免 totalTime 随 React 渲染变化导致 rAF/rvfc 重绑 */
    const playheadTotalTimeRef = useRef(0);
    {
        const te = timelineEditorRef.current;
        const fromEditor = te && te.totalTime > 0 ? te.totalTime : 0;
        playheadTotalTimeRef.current = fromEditor > 0 ? fromEditor : duration || 0;
    }

    // 将高频播放时钟写入 Zustand，避免整页 VideoEdit 因 currentTime 高频更新而重渲染
    const setPlaybackUiTime = useCallback((t: number) => {
        currentTimeRef.current = t;
        usePlaybackStore.getState().setCurrentTime(t);
    }, []);

        const timelineScrubbingRef = useRef(false);

        const onTimelineSegmentSelectionChange = useCallback((id: number | null) => {
        setTimelineSelectedSubtitleId(id);
    }, []);
    const onTimelineActiveTrackChange = useCallback((track: 'translated' | 'original') => {
        setTimelineActiveTrack(track);
    }, []);

        class TimelineEditor {
        isPlaying: boolean;
        currentTime: number;
        totalTime: number;
        zoomLevel: number;
        audioEnabled: boolean;
        segments: any[];
        activeTrack: string;
        onZoomChange?: (value: number) => void;
        onPlayheadMove?: (time: number) => void;
        lastScale: number;
        playheadAnimationId?: number; // 用于requestAnimationFrame的ID
        updateCount?: number; // 用于调试的更新计数器
        private videoData?: any; // 保存videoData用于访问字幕数据
        /** 翻译轨道片段由 React 渲染时，与页面选中态同步 */
        private onSegmentSelectionChange?: (id: number | null) => void;
        private onActiveTrackChange?: (track: 'translated' | 'original') => void;
        // 用于保存 drag 相关的监听器引用，防止重复添加
        private dragMouseDownHandler?: (e: MouseEvent) => void;
        private dragMouseMoveHandler?: (e: MouseEvent) => void;
        private dragMouseUpHandler?: () => void;
        private isDragApiCalling: boolean = false; // 防止拖动时重复调用 API
        private dragApiRequestRef: Promise<void> | null = null; // 保存正在进行的拖动 API 请求
        // 用于设置倍速调节加载状态的回调函数
        private setIsAdjustingSpeed?: (loading: boolean) => void;
        private setVideoData?: React.Dispatch<React.SetStateAction<Task.GetTaskStatusResult | null>>;
        private setTargetText?: React.Dispatch<React.SetStateAction<any[]>>;
        private setPendingTimelineUpdates?: React.Dispatch<React.SetStateAction<Map<number, { audio_speed: number; post_start_time: string; post_end_time: string }>>>;
        // z-index 管理：用于处理重叠片段的层级
        private maxZIndex: number = 1; // 当前最高的 z-index 值
        // 记录在 mousedown 阶段被自动选中的片段，用于吞掉紧随其后的重复 click
        private skipNextSegmentClickTarget: HTMLElement | null = null;
        // 用于保存 playhead 相关的监听器引用
        private playheadMouseDownHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseDownHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseMoveHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseUpHandler?: () => void;
        private detachTimelineScrollbar?: () => void;
        private timelineScrubbingRef?: React.MutableRefObject<boolean>;
        private isFromPure: boolean;
        private onOriginTimestampSaved?: (subtitleId: number, originStart: string, originEnd: string) => void;
        private originTrackResizePointerDown?: (e: PointerEvent) => void;
        private originTrackContentEl?: HTMLElement | null;
        private segmentPlayingLockLineIdRef?: React.MutableRefObject<number | null>;
        
        constructor(
            onZoomChange?: (value: number) => void,
            onPlayheadMove?: (time: number) => void,
            videoData?: any,
            setIsAdjustingSpeed?: (loading: boolean) => void,
            timelineScrubbingRef?: React.MutableRefObject<boolean>,
            onSegmentSelectionChange?: (id: number | null) => void,
            onActiveTrackChange?: (track: 'translated' | 'original') => void,
            setVideoData?: React.Dispatch<React.SetStateAction<Task.GetTaskStatusResult | null>>,
            setTargetText?: React.Dispatch<React.SetStateAction<any[]>>,
            setPendingTimelineUpdates?: React.Dispatch<React.SetStateAction<Map<number, { audio_speed: number; post_start_time: string; post_end_time: string }>>>,
            isFromPure: boolean = false,
            onOriginTimestampSaved?: (subtitleId: number, originStart: string, originEnd: string) => void,
            segmentPlayingLockLineIdRef?: React.MutableRefObject<number | null>
        ) {
            this.videoData = videoData;
            this.setIsAdjustingSpeed = setIsAdjustingSpeed;
            this.timelineScrubbingRef = timelineScrubbingRef;
            this.setVideoData = setVideoData;
            this.setTargetText = setTargetText;
            this.setPendingTimelineUpdates = setPendingTimelineUpdates;
            this.isFromPure = isFromPure;
            this.onOriginTimestampSaved = onOriginTimestampSaved;
            this.segmentPlayingLockLineIdRef = segmentPlayingLockLineIdRef;
            this.onSegmentSelectionChange = onSegmentSelectionChange;
            this.onActiveTrackChange = onActiveTrackChange;
            this.isPlaying = false;
            this.currentTime = 0;
            this.totalTime = this.calculateTotalTime(videoData); // 动态计算总时间
            this.zoomLevel = 25; // 默认缩放级别设为10%
            this.audioEnabled = false;
            this.segments = [];
            this.activeTrack = 'translated'; // 'translated' or 'original'
            this.onZoomChange = onZoomChange;
            this.onPlayheadMove = onPlayheadMove;
            this.lastScale = 1; // 初始化上次缩放比例
            this.maxZIndex = 1; // 初始化 z-index 计数器
            
            this.init();
        }
        
        // 更新videoData引用
        updateVideoData(videoData: any) {
            this.videoData = videoData;
        }
        
        init() {
            this.bindEvents();
            this.generateTimeMarks();
            // 如果存在翻译后轨道，则默认激活翻译后轨道；否则（例如纯净版模式隐藏翻译后轨道）激活原视频轨道
            const translatedTrack = document.getElementById('translatedTrack');
            const defaultTrack: 'translated' | 'original' = translatedTrack ? 'translated' : 'original';
            this.setActiveTrack(defaultTrack);
            this.initPlayhead();
            this.initSegmentDrag(); // 初始化字幕拖拽功能
            
            // 应用初始缩放，确保时间轴宽度正确
            this.applyZoom();
        }
        
        // 初始化字幕片段
        initSubtitles(subtitles: Task.SubtitlesLegacy[]) {
            if (subtitles && subtitles.length > 0) {
                this.generateSegmentsFromSubtitles(subtitles);
            }
        }
        
        // 更新特定字幕片段的时间戳和文本
        updateSegment(subtitleId: number, subtitle: Task.SubtitlesLegacy) {
            const segments = document.querySelectorAll(`[data-subtitle-id="${subtitleId}"]`);
            
            if (segments.length === 0) return;
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                if (element.dataset.reactTranslatedSegment === '1') {
                    return;
                }
                const track = element.closest('.track');
                
                if (track?.id === 'translatedTrack') {
                    // 更新翻译后轨道片段
                    const start = parseTimeToSeconds(subtitle.post_start_time || subtitle.origin_start_time);
                    const end = parseTimeToSeconds(subtitle.post_end_time || subtitle.origin_end_time);
                    const text = subtitle.post_text || ''; // 只使用post_text，不要回退到origin_text
                    
                    element.dataset.start = start.toString();
                    element.dataset.end = end.toString();
                    element.querySelector('span')!.textContent = text;
                } else if (track?.id === 'originalTrack') {
                    // 更新原视频轨道片段
                    const start = parseTimeToSeconds(subtitle.origin_start_time);
                    const end = parseTimeToSeconds(subtitle.origin_end_time);
                    const text = subtitle.origin_text || '';
                    
                    element.dataset.start = start.toString();
                    element.dataset.end = end.toString();
                    element.querySelector('span')!.textContent = text;
                }
            });
            
            // 重新定位片段
            this.positionSegments();
        }
        
        // 计算时间轴总时长
        calculateTotalTime(videoData?: any) {
            // 开始计算时间轴总时长
            
            // 获取字幕数据（用于计算最大结束时间）
            let subtitles: any[] = [];
            if (videoData && videoData.subtitles) {
                subtitles = videoData.subtitles;
                // 使用传入的videoData
            } else {
                // 从全局videoData中获取字幕数据
                const videoDataElement = document.querySelector('[data-video-data]');
                if (videoDataElement) {
                    try {
                        const data = JSON.parse(videoDataElement.getAttribute('data-video-data') || '{}');
                        subtitles = data.subtitles || [];
                        // 从DOM获取videoData
                    } catch (error) {
                        console.error('解析videoData时出错:', error);
                    }
                } else {
                    // 未找到videoData元素
                }
            }
            
            // 计算字幕的最大结束时间
            let maxEndTime = 0;
            if (subtitles.length > 0) {
                subtitles.forEach((subtitle: any, index: number) => {
                    // 优先使用post_end_time，如果没有则使用origin_end_time
                    const endTimeStr = subtitle.post_end_time || subtitle.origin_end_time || '00:00:00,000';
                    
                    // 将时间字符串转换为秒数 (格式: HH:MM:SS,mmm)
                    const timeParts = endTimeStr.split(':');
                    const hours = parseInt(timeParts[0] || '0');
                    const minutes = parseInt(timeParts[1] || '0');
                    const secondsParts = timeParts[2] ? timeParts[2].split(',') : ['0', '0'];
                    const seconds = parseInt(secondsParts[0] || '0');
                    const milliseconds = parseInt(secondsParts[1] || '0');
                    const endTime = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
                    
                    if (endTime > maxEndTime) {
                        maxEndTime = endTime;
                    }
                });
            }
            
            // 优先使用视频的实际时长（不向上取整）
            const videoElement = document.querySelector('video') as HTMLVideoElement;
            if (videoElement && videoElement.duration && !isNaN(videoElement.duration)) {
                const videoDuration = videoElement.duration;
                // 使用视频实际时长
                // 使用视频实际时长作为上限，确保不超过视频实际时长（不向上取整）
                // 如果字幕结束时间超过视频时长，使用视频实际时长；否则使用视频实际时长（因为视频时长已经包含了所有内容）
                const totalTime = Math.max(20, videoDuration);
                // 计算得到的总时长
                return totalTime;
            }
            
            // 如果无法获取视频时长，则从videoData中获取
            if (videoData && videoData.duration) {
                const videoDuration = videoData.duration;
                // 使用videoData中的时长
                // 使用videoData中的时长，不向上取整
                const totalTime = Math.max(20, videoDuration);
                // 计算得到的总时长
                return totalTime;
            }
            
            // 最后才使用字幕数据计算（作为备选方案）
            if (subtitles.length === 0) {
                // 没有字幕数据，使用默认20秒
                return 20; // 默认20秒
            }
            
            // 使用字幕结束时间，但不添加额外的缓冲时间，也不向上取整
            const totalTime = Math.max(20, maxEndTime);
            // 使用字幕数据计算的总时长
            return totalTime;
        }
        
        bindEvents() {
            // 轨道标签点击事件 - 更新为新的左侧面板选择器
            document.querySelectorAll('.timeline-left-panel .track-label').forEach((label, index) => {
                label.addEventListener('click', () => {
                    const trackType = index === 0 ? 'translated' : 'original';
                    // 关键：轨道按钮切换除了更新 UI 之外，还要切换音频（停止旧轨声音，必要时开始新轨声音）
                    // handleSwitchTrack 内部会调用 timelineEditorRef.current.setActiveTrack，从而保持 UI 和音频一致。
                    onSwitchTrack(trackType);
                });
            });
            
            // 滚动条事件
            this.initScrollbar();
            
            // 片段点击事件（仅原视频轨道：翻译轨道由 React 组件自行派发交互）
            document.querySelectorAll('#originalTrack .segment').forEach(segment => {
                segment.addEventListener('click', (e) => {
                    const target = e.target as HTMLElement;
                    const targetSegment = target.closest('.segment') as HTMLElement;
                    if (targetSegment) {
                        // 如果点击的不是当前激活轨道的片段，先切换轨道
                        const track = targetSegment.closest('.track') as HTMLElement;
                        const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
                        
                        if (this.activeTrack !== trackType) {
                            this.setActiveTrack(trackType);
                        }
                        
                        this.selectSegment(targetSegment);
                    }
                });
            });
            
            // 字幕拖拽调节功能现在在initSegmentDrag中处理
            
            // 轨道点击事件
            this.initTrackClick();
            
            // 缩放控制事件
            this.initZoomControls();
        }
        
        generateTimeMarks() {
            const timeMarksContainer = document.getElementById('timeMarks');
            if (!timeMarksContainer) return;
            
            // 检查是否需要重新生成时间标记（避免不必要的DOM操作）
            const currentScale = calcTimelineZoomScale(this.zoomLevel);
            if (this.lastScale && Math.abs(currentScale - this.lastScale) < 0.05) {
                return; // 缩放变化很小，不需要重新生成
            }
            
            // 使用DocumentFragment减少DOM操作
            const fragment = document.createDocumentFragment();
            
            const startTime = 0;
            const endTime = this.totalTime; // 使用动态计算的总时长
            const totalRange = endTime - startTime;
            
            // 根据缩放级别动态调整刻度间隔，减少DOM元素数量
            let majorInterval, minorInterval, microInterval;
            if (currentScale < 1.0) {
                // 缩小状态：减少刻度密度
                majorInterval = 5; // 主刻度间隔5秒
                minorInterval = 1; // 次刻度间隔1秒
                microInterval = 0.2; // 微刻度间隔0.2秒
            } else if (currentScale > 1.5) {
                // 放大状态：增加刻度密度
                majorInterval = 0.5; // 主刻度间隔0.5秒
                minorInterval = 0.1; // 次刻度间隔0.1秒
                microInterval = 0.02; // 微刻度间隔0.02秒
            } else {
                // 正常状态：固定刻度间隔
                majorInterval = 1; // 主刻度间隔1秒
                minorInterval = 0.1; // 次刻度间隔0.1秒
                microInterval = 0.01; // 微刻度间隔0.01秒
            }
            
            // 生成主刻度
            for (let time = startTime; time <= endTime; time += majorInterval) {
                const mark = document.createElement('div');
                mark.className = 'time-mark major';
                mark.style.left = `${(time / totalRange) * 100}%`;
                
                // 00:00.0的刻度不显示数字
                if (time > 0) {
                    mark.setAttribute('data-time', this.formatTime(time));
                }
                
                fragment.appendChild(mark);
            }
            
            // 生成次刻度
            for (let time = startTime; time <= endTime; time += minorInterval) {
                if (time % majorInterval !== 0) {
                    const mark = document.createElement('div');
                    mark.className = 'time-mark minor';
                    mark.style.left = `${(time / totalRange) * 100}%`;
                    fragment.appendChild(mark);
                }
            }
            
            // 生成微刻度（仅在缩放级别较高时显示）
            if (this.zoomLevel > 70) { // 当缩放级别超过70%时显示微刻度
                for (let time = startTime; time <= endTime; time += microInterval) {
                    if (time % minorInterval !== 0) { // 跳过次刻度
                        const mark = document.createElement('div');
                        mark.className = 'time-mark micro';
                        mark.style.left = `${(time / totalRange) * 100}%`;
                        fragment.appendChild(mark);
                    }
                }
            }
            
            // 一次性更新DOM
            timeMarksContainer.innerHTML = '';
            timeMarksContainer.appendChild(fragment);
        }
        
        // 根据字幕数据生成时间轴片段
        generateSegmentsFromSubtitles(subtitles: Task.SubtitlesLegacy[]) {
            const originalTrack = document.getElementById('originalTrack');
            
            // 如果连原视频轨道都不存在，则无法生成片段
            if (!originalTrack) return;
            
            // 重置 z-index 计数器（重新生成片段时）
            this.maxZIndex = 1;
            
            // 翻译后轨道由 React（TranslatedTimelineSegments）渲染，不在此清空或生成
            originalTrack.querySelector('.track-content')!.innerHTML = '';
            
            // 为每个字幕创建片段（仅原视频轨道；翻译轨道见 TranslatedTimelineSegments）
            subtitles.forEach((subtitle, index) => {
                // 原视频轨道片段（使用origin_text和origin时间戳）
                const originalStart = parseTimeToSeconds(subtitle.origin_start_time);
                const originalEnd = parseTimeToSeconds(subtitle.origin_end_time);
                const originalText = subtitle.origin_text || '';
                
                const originalSegment = document.createElement('div');
                originalSegment.className = 'segment';
                originalSegment.dataset.start = originalStart.toString();
                originalSegment.dataset.end = originalEnd.toString();
                originalSegment.dataset.subtitleId = subtitle.id.toString();
                // 初始化 z-index（基础值，点击时会提升）
                (originalSegment as HTMLElement).style.zIndex = '1';
                
                const originalSpan = document.createElement('span');
                originalSpan.textContent = originalText;
                if (this.isFromPure) {
                    originalSegment.setAttribute('data-origin-timestamp-editable', '1');
                    const hL = document.createElement('div');
                    hL.className = 'subtitle-resize-handle subtitle-resize-handle--left';
                    const hR = document.createElement('div');
                    hR.className = 'subtitle-resize-handle subtitle-resize-handle--right';
                    originalSegment.appendChild(hL);
                    originalSegment.appendChild(originalSpan);
                    originalSegment.appendChild(hR);
                    originalSegment.dataset.originalStart = String(originalStart);
                    originalSegment.dataset.originalEnd = String(originalEnd);
                    originalSegment.dataset.originalSpeed = '1';
                } else {
                    originalSegment.appendChild(originalSpan);
                }
                
                originalTrack.querySelector('.track-content')!.appendChild(originalSegment);
            });
            
            // 重新绑定片段点击事件
            this.bindSegmentEvents();
            
            // 重新定位片段
            this.positionSegments();
            
            // 重新初始化拖拽功能（因为DOM元素已更新）
            this.initSegmentDrag();
            this.teardownOriginTrackResize();
            if (this.isFromPure) {
                this.bindOriginTrackResize();
            }
        }

        teardownOriginTrackResize() {
            if (this.originTrackContentEl && this.originTrackResizePointerDown) {
                this.originTrackContentEl.removeEventListener('pointerdown', this.originTrackResizePointerDown, true);
            }
            this.originTrackContentEl = null;
            this.originTrackResizePointerDown = undefined;
        }

        /** 纯净版：原轨左右手柄调节时间窗（不调语速） */
        bindOriginTrackResize() {
            this.teardownOriginTrackResize();
            if (!this.isFromPure) {
                return;
            }
            const tc = document.querySelector('#originalTrack .track-content') as HTMLElement | null;
            if (!tc) {
                return;
            }
            this.originTrackContentEl = tc;
            this.originTrackResizePointerDown = (e: PointerEvent) => {
                if (!this.isFromPure) {
                    return;
                }
                const h = (e.target as HTMLElement).closest('.subtitle-resize-handle');
                if (!h) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                const root = h.closest('.segment') as HTMLElement | null;
                if (!root) {
                    return;
                }
                const side = h.classList.contains('subtitle-resize-handle--left') ? 'left' : 'right';
                const subtitleId = parseInt(root.dataset.subtitleId || '0', 10);
                if (!Number.isFinite(subtitleId) || subtitleId <= 0) {
                    return;
                }
                const trackContent = root.closest('.track-content') as HTMLElement;
                if (!trackContent) {
                    return;
                }
                const total = this.totalTime;
                if (total <= 0) {
                    return;
                }
                const buildSorted = (): { sorted: IndexedSegment[]; index: number } => {
                    const roots = Array.from(
                        document.querySelectorAll('#originalTrack .track-content .segment')
                    ) as HTMLElement[];
                    const indexed: IndexedSegment[] = roots.map((element) => ({
                        element,
                        start: parseFloat(element.dataset.start || '0'),
                        end: parseFloat(element.dataset.end || '0'),
                    }));
                    const sorted = sortSegmentsByStart(indexed);
                    const index = sorted.findIndex((s) => s.element === root);
                    return { sorted, index };
                };
                let raf: number | null = null;
                const activeId = e.pointerId;
                try {
                    root.setPointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                document.body.style.userSelect = 'none';

                const onMove = (ev: PointerEvent) => {
                    if (ev.pointerId !== activeId) {
                        return;
                    }
                    if (raf !== null) {
                        cancelAnimationFrame(raf);
                    }
                    raf = requestAnimationFrame(() => {
                        raf = null;
                        const rect = trackContent.getBoundingClientRect();
                        const t = clientXToTime(ev.clientX, rect, total);
                        const { sorted, index } = buildSorted();
                        if (index < 0) {
                            return;
                        }
                        const endT = parseFloat(root.dataset.end || '0');
                        const startT = parseFloat(root.dataset.start || '0');
                        let next: { start: number; end: number } | null = null;
                        if (side === 'left') {
                            next = computeOriginTimestampResizeLeft({
                                newTimeFromPointer: t,
                                endTime: endT,
                                currentIndex: index,
                                sortedSegments: sorted,
                            });
                        } else {
                            next = computeOriginTimestampResizeRight({
                                newTimeFromPointer: t,
                                startTime: startT,
                                currentIndex: index,
                                sortedSegments: sorted,
                            });
                        }
                        if (!next) {
                            return;
                        }
                        root.dataset.start = next.start.toFixed(3);
                        root.dataset.end = next.end.toFixed(3);
                        this.positionSegments();
                    });
                };

                const onEnd = (ev: PointerEvent | null) => {
                    if (ev && ev.pointerId !== activeId) {
                        return;
                    }
                    if (raf !== null) {
                        cancelAnimationFrame(raf);
                        raf = null;
                    }
                    root.removeEventListener('pointermove', onMove);
                    root.removeEventListener('pointerup', onUp);
                    root.removeEventListener('pointercancel', onUp);
                    try {
                        root.releasePointerCapture(activeId);
                    } catch {
                        /* ignore */
                    }
                    document.body.style.userSelect = '';
                    const s = parseFloat(root.dataset.start || '0');
                    const en = parseFloat(root.dataset.end || '0');
                    void this.updateOriginSubtitleTimestamps(subtitleId, s, en).then(() => {
                        root.dataset.originalStart = s.toFixed(3);
                        root.dataset.originalEnd = en.toFixed(3);
                        this.positionSegments();
                    });
                };
                const onUp = (ev: PointerEvent) => {
                    onEnd(ev);
                };

                root.addEventListener('pointermove', onMove);
                root.addEventListener('pointerup', onUp);
                root.addEventListener('pointercancel', onUp);
            };
            tc.addEventListener('pointerdown', this.originTrackResizePointerDown, true);
        }
        
        // 绑定片段点击事件（仅原视频轨道；翻译轨道由 React 处理）
        bindSegmentEvents() {
            document.querySelectorAll('#originalTrack .segment').forEach(segment => {
                segment.addEventListener('click', (e) => {
                    if ((e.target as HTMLElement).closest('.subtitle-resize-handle')) {
                        return;
                    }
                    e.stopPropagation();
                    const targetSegment = segment as HTMLElement;

                    if (this.skipNextSegmentClickTarget === targetSegment) {
                        this.skipNextSegmentClickTarget = null;
                        const track = targetSegment.closest('.track') as HTMLElement;
                        const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
                        const sid = parseInt(targetSegment.dataset.subtitleId || '0', 10);
                        this.onSegmentSelectionChange?.(Number.isFinite(sid) && sid > 0 ? sid : null);
                        this.notifySegmentSelected(targetSegment, trackType);
                        return;
                    }
                    
                    // 如果点击的不是当前激活轨道的片段，先切换轨道
                    const track = segment.closest('.track') as HTMLElement;
                    const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
                    
                    if (this.activeTrack !== trackType) {
                        this.setActiveTrack(trackType);
                    }
                    
                    this.selectSegment(targetSegment);
                });
            });
        }
        
        // 初始化字幕片段拖拽功能
        initSegmentDrag() {
            // 如果已经绑定过事件，先移除旧的监听器
            if (this.dragMouseDownHandler) {
                document.removeEventListener('mousedown', this.dragMouseDownHandler);
            }
            if (this.dragMouseMoveHandler) {
                document.removeEventListener('mousemove', this.dragMouseMoveHandler);
            }
            if (this.dragMouseUpHandler) {
                document.removeEventListener('mouseup', this.dragMouseUpHandler);
            }
            
            let isDragging = false;
            let currentSegment: HTMLElement | null = null;
            let dragStartX = 0;
            let isDragApiTriggered = false; // 标记本次拖动是否已触发API
            let dragStartTime = 0;
            let dragStartY = 0; // 添加Y坐标，用于计算拖动距离
            let hasMoved = false; // 添加标志位，判断是否真的发生了拖动
            let animationFrameId: number | null = null;
            let dragTargetTrack: 'translated' | 'original' = 'translated';
            
            this.dragMouseDownHandler = (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.subtitle-resize-handle')) {
                    return;
                }
                // 查找最近的字幕元素
                const segment = (e.target as HTMLElement).closest('.segment') as HTMLElement;
                if (!segment) return;

                const originalTrack = document.getElementById('originalTrack');
                const translatedTrack = document.getElementById('translatedTrack');
                const isInOriginalTrack = !!originalTrack && originalTrack.contains(segment);
                const isInTranslatedTrack = !!translatedTrack && translatedTrack.contains(segment);
                const isPureOrigin = Boolean(isInOriginalTrack && this.isFromPure);

                // 编辑页 / 非纯净：原视频轨仍不可拖动
                if (isInOriginalTrack && !this.isFromPure) {
                    return;
                }

                if (!isInTranslatedTrack && !isPureOrigin) {
                    return;
                }

                if (isPureOrigin) {
                    this.skipNextSegmentClickTarget = segment;
                    this.setActiveTrack('original');
                    this.selectSegment(segment, { notifySelection: false });
                    dragTargetTrack = 'original';
                } else {
                    // 翻译轨道：未选中时先自动选中
                    if (isInTranslatedTrack && !segment.classList.contains('active')) {
                        this.skipNextSegmentClickTarget = segment;
                        this.setActiveTrack('translated');
                        this.selectSegment(segment, { notifySelection: false });
                    } else {
                        this.skipNextSegmentClickTarget = null;
                    }
                    dragTargetTrack = 'translated';
                }

                // 仅：纯净原轨 或 翻译轨 参与拖拽
                if (!isPureOrigin && !isInTranslatedTrack) return;

                // 左右边界调节由 React SubtitleResizeHandle + useSubtitleResize 处理
                currentSegment = segment;
                dragStartX = e.clientX;
                dragStartY = e.clientY;
                dragStartTime = parseFloat(segment.dataset.start || '0');
                hasMoved = false;
                isDragApiTriggered = false;

                (currentSegment as any).originalStart = dragStartTime;
                (currentSegment as any).originalEnd = parseFloat(segment.dataset.end || '0');

                e.preventDefault();
                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'grab';
            };
            
            document.addEventListener('mousedown', this.dragMouseDownHandler);
            
            // 使用 requestAnimationFrame 优化拖拽性能
            this.dragMouseMoveHandler = (e: MouseEvent) => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
                
                animationFrameId = requestAnimationFrame(() => {
                    if (currentSegment && !isDragging) {
                        // 检查是否真的发生了拖动（移动距离超过3px）
                        const deltaX = Math.abs(e.clientX - dragStartX);
                        const deltaY = Math.abs(e.clientY - dragStartY);
                        const moveDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                        
                        if (moveDistance > 3) {
                            // 移动距离超过阈值，开始真正的拖动
                            isDragging = true;
                            hasMoved = true;
                            document.body.style.cursor = 'grabbing';
                        }
                    }
                    
                    if (isDragging && currentSegment) {
                        // 拖动整个字幕
                        const trackContent = currentSegment.closest('.track-content') as HTMLElement;
                        const trackRect = trackContent.getBoundingClientRect();
                        const deltaX = e.clientX - dragStartX;
                        const deltaTime = (deltaX / trackRect.width) * this.totalTime;
                        
                        // 使用记录的原始时间，确保宽度不变
                        const originalStart = (currentSegment as any).originalStart;
                        const originalEnd = (currentSegment as any).originalEnd;
                        const duration = originalEnd - originalStart; // 保持原始宽度
                        
                        let newStart = originalStart + deltaTime;
                        let newEnd = newStart + duration; // 确保宽度不变
                        
                        // 边界检查
                        newStart = Math.max(0, Math.min(newStart, this.totalTime - duration));
                        newEnd = newStart + duration; // 重新计算结束时间以保持宽度
                        
                        // 获取同一轨道上的所有字幕（排除当前字幕）
                        const allSegments = Array.from(trackContent.querySelectorAll('.segment'))
                            .filter(seg => seg !== currentSegment)
                            .map(seg => ({
                                element: seg,
                                start: parseFloat((seg as HTMLElement).dataset.start || '0'),
                                end: parseFloat((seg as HTMLElement).dataset.end || '0')
                            }))
                            .sort((a, b) => a.start - b.start);
                        
                        // 检查与其他字幕的重叠，如果重叠则调整位置
                        let finalStart = newStart;
                        let finalEnd = newEnd;
                        
                        for (const segment of allSegments) {
                            if (finalStart < segment.end && finalEnd > segment.start) {
                                // 有重叠，选择最近的合法位置
                                const leftGap = segment.start - finalEnd;
                                const rightGap = finalStart - segment.end;
                                
                                if (leftGap >= 0) {
                                    // 可以放在左侧
                                    finalStart = segment.start - duration;
                                    finalEnd = segment.start;
                                } else if (rightGap >= 0) {
                                    // 可以放在右侧
                                    finalStart = segment.end;
                                    finalEnd = segment.end + duration;
                                } else {
                                    // 重叠，选择距离最近的位置
                                    const center = (segment.start + segment.end) / 2;
                                    if (newStart < center) {
                                        finalStart = segment.start - duration;
                                        finalEnd = segment.start;
                                    } else {
                                        finalStart = segment.end;
                                        finalEnd = segment.end + duration;
                                    }
                                }
                                
                                // 重新检查边界
                                finalStart = Math.max(0, Math.min(finalStart, this.totalTime - duration));
                                finalEnd = finalStart + duration;
                            }
                        }
                        
                        // 始终更新位置，确保跟随鼠标
                        currentSegment.dataset.start = finalStart.toFixed(3);
                        currentSegment.dataset.end = finalEnd.toFixed(3);
                        
                        // 直接更新当前字幕的位置，避免重新计算所有字幕
                        const leftPercent = (finalStart / this.totalTime) * 100;
                        const widthPercent = (duration / this.totalTime) * 100;
                        currentSegment.style.left = `${leftPercent}%`;
                        currentSegment.style.width = `${widthPercent}%`;
                    }
                });
            };
            
            document.addEventListener('mousemove', this.dragMouseMoveHandler);
            
            this.dragMouseUpHandler = () => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                
                // 只有当真正发生了拖动（hasMoved为true）且本次拖动还未触发API时才调用API
                if (isDragging && currentSegment && hasMoved && !isDragApiTriggered) {
                    // 标记已触发，防止重复调用
                    isDragApiTriggered = true;
                    
                    // 如果有正在进行的拖动API请求，先取消它（使用最新的拖动结果）
                    if (this.isDragApiCalling && this.dragApiRequestRef) {
                        console.log('取消之前的拖动API请求，使用最新的拖动结果');
                        // 不等待之前的请求完成，直接使用最新的拖动结果
                    }
                    
                    // 拖动结束后重新计算所有字幕位置，确保一致性
                    this.positionSegments();
                    
                    // 输出拖拽后的时间戳到控制台
                    const startTime = parseFloat(currentSegment.dataset.start || '0');
                    const endTime = parseFloat(currentSegment.dataset.end || '0');
                    const segmentText = currentSegment.querySelector('span')?.textContent || '';
                    const subtitleId = parseInt(currentSegment.dataset.subtitleId || '0');
                    
                    // 字幕片段拖拽完成
                    
                    if (dragTargetTrack === 'original' && this.isFromPure) {
                        this.updateOriginSubtitleTimestamps(subtitleId, startTime, endTime).finally(() => {
                            isDragApiTriggered = false;
                        });
                    } else {
                        this.updateSubtitleTimestamps(subtitleId, startTime, endTime).finally(() => {
                            isDragApiTriggered = false;
                        });
                    }
                }
                
                isDragging = false;
                currentSegment = null;
                hasMoved = false;
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            };
            
            document.addEventListener('mouseup', this.dragMouseUpHandler);
        }
        
        // 格式化时间显示
        formatTime(seconds: number): string {
            return formatTimelineTime(seconds);
        }
        
        // 计算并更新倍速值
        async calculateAndUpdateSpeed(segment: HTMLElement, subtitleId: number, newStartTime: number, newEndTime: number) {
            const revertTranslatedSegmentFromVideoData = () => {
                const sub = this.videoData?.subtitles?.find((s: any) => s.id === subtitleId) as Task.SubtitlesLegacy | undefined;
                if (!sub) return;
                const start = parseTimeToSeconds(sub.post_start_time || sub.origin_start_time);
                const end = parseTimeToSeconds(sub.post_end_time || sub.origin_end_time);
                segment.dataset.start = start.toFixed(3);
                segment.dataset.end = end.toFixed(3);
                segment.dataset.originalStart = start.toFixed(3);
                segment.dataset.originalEnd = end.toFixed(3);
                segment.dataset.originalSpeed = String(sub.audio_speed ?? 1);
                this.positionSegments();
            };

            try {
                // 显示加载遮罩
                if (this.setIsAdjustingSpeed) {
                    this.setIsAdjustingSpeed(true);
                }
                
                // 新倍速必须与 subtitleTimelineResizeMath 一致，否则拖边允许、松手却用另一套公式会误判超界并回弹且不发请求
                const originalStartDs = parseFloat(segment.dataset.originalStart || '0');
                const originalEndDs = parseFloat(segment.dataset.originalEnd || '0');
                const originalSpeedDs = parseFloat(segment.dataset.originalSpeed || '1');
                const baselineDuration = originalEndDs - originalStartDs;

                // 计算新的时长
                const newDuration = newEndTime - newStartTime;

                let newSpeed: number;
                if (baselineDuration > 0 && Number.isFinite(originalSpeedDs) && originalSpeedDs > 0) {
                    newSpeed = (baselineDuration * originalSpeedDs) / newDuration;
                } else {
                    // 无有效拖边基准时回退：与「原始音频时长 / 新槽位时长」一致
                    let originDuration = 0;
                    if (this.videoData?.subtitles) {
                        const subtitle = this.videoData.subtitles.find((sub: any) => sub.id === subtitleId);
                        if (subtitle) {
                            if (typeof subtitle.origin_post_time === 'number' && !Number.isNaN(subtitle.origin_post_time)) {
                                originDuration = subtitle.origin_post_time;
                            } else if (subtitle.origin_start_time && subtitle.origin_end_time) {
                                const originStart = parseTimeToSeconds(subtitle.origin_start_time);
                                const originEnd = parseTimeToSeconds(subtitle.origin_end_time);
                                originDuration = originEnd - originStart;
                            }
                        }
                    }
                    if (originDuration === 0) {
                        const originalStart = parseFloat(segment.dataset.originalStart || '0');
                        const originalEnd = parseFloat(segment.dataset.originalEnd || '0');
                        originDuration = originalEnd - originalStart;
                    }
                    newSpeed = originDuration / newDuration;
                }
                
                // 保留三位小数
                const roundedSpeed = Math.round(newSpeed * 1000) / 1000;
                
                // 倍速计算完成
                
                // 检查倍速是否在有效范围内
                if (roundedSpeed < 0.5 || roundedSpeed > 2.0) {
                    console.warn('倍速超出范围，无法更新:', roundedSpeed);
                    revertTranslatedSegmentFromVideoData();
                    return;
                }
                
                const postStartTime = formatSecondsToTimestamp(newStartTime);
                const postEndTime = formatSecondsToTimestamp(newEndTime);
                
                // 获取当前字幕的volumeGainDb，如果没有则默认为1.0
                const currentSubtitle = this.videoData?.subtitles?.find((sub: Task.SubtitlesLegacy) => sub.id === subtitleId);
                const currentVolumeGainDb = currentSubtitle?.volume_gain_db ?? 1.0;
                
                // 调用统一接口，applyToAll固定为false（调节倍速时）
                // 传入时间戳信息，避免"数据已被修改"错误
                const response = await adjustVolumeAndSpeedApi({
                    subtitleId: subtitleId,
                    volumeGainDb: currentVolumeGainDb,  // 保持当前音量不变
                    audioSpeed: roundedSpeed,  // 新的倍速值
                    applyToAll: false,  // 调节倍速时固定为false
                    postStartTime: postStartTime,  // 传入新的开始时间戳
                    postEndTime: postEndTime  // 传入新的结束时间戳
                });
                
                if (response.code === 0 && response.data && response.data.length > 0) {
                    const updatedSubtitle = response.data[0];
                    // 时间轴调节倍速成功
                    
                    // 同步更新volumeGainDb和audioSpeed到本地数据
                    const updatedVolumeGainDb = updatedSubtitle.volumeGainDb ?? currentVolumeGainDb;
                    const updatedAudioSpeed = updatedSubtitle.audioSpeed ?? roundedSpeed;
                    
                    // 更新segment的原始倍速数据
                    segment.dataset.originalSpeed = roundedSpeed.toString();
                    
                    // 直接更新targetText中的时间范围，不触发videoData更新
                    this.setTargetText?.(prev => prev.map(item => {
                        if (item.id === subtitleId) {
                            return {
                                ...item,
                                timeRange: `${postStartTime} → ${postEndTime}`
                            };
                        }
                        return item;
                    }));
                    
                    // 采用"改哪个动哪个"的原则，将更新数据存储到待同步队列
                    this.setPendingTimelineUpdates?.(prev => {
                        const newMap = new Map(prev);
                        newMap.set(subtitleId, {
                            audio_speed: updatedAudioSpeed,
                            post_start_time: postStartTime,
                            post_end_time: postEndTime
                        });
                        return newMap;
                    });
                    
                    // 时间轴调节完成，数据已更新到segment和targetText，待同步到videoData
                    
                    // 立即更新videoData中的audio_speed和volume_gain_db，确保下次播放时使用新值
                    this.setVideoData?.(prev => {
                        if (!prev?.subtitles) return prev;
                        
                        const updatedSubtitles = prev.subtitles.map(subtitle => {
                            if (subtitle.id === subtitleId) {
                                return {
                                    ...subtitle,
                                    audio_speed: updatedAudioSpeed,
                                    volume_gain_db: updatedVolumeGainDb,
                                    post_start_time: postStartTime,
                                    post_end_time: postEndTime
                                };
                            }
                            return subtitle;
                        });
                        
                        return {
                            ...prev,
                            subtitles: updatedSubtitles
                        };
                    });
                    
                    // 如果当前正在播放该字幕的音频，实时更新语速
                    const audioId = `target_audio_${subtitleId}`;
                    
                    // 检查AudioManager播放的音频（通过playTargetSubtitleAudio播放）
                    if (audioManager.isPlaying(audioId)) {
                        audioManager.setPlaybackRate(audioId, updatedAudioSpeed);
                        // 时间轴调节：实时更新AudioManager音频语速
                    }
                    
                    // 检查原生Audio播放的翻译音频（通过playTransAudioByTime自动播放）
                    if (transAudioRef?.current && !transAudioRef.current.paused && currentPlayingSubtitleIdRef?.current === subtitleId) {
                        transAudioRef.current.playbackRate = updatedAudioSpeed;
                        // 时间轴调节：实时更新原生Audio翻译音频语速
                    }
                    
                    // 检查全局播放实例（通过playTargetSubtitleAudio播放的回退模式）
                    if (currentPlayingInstanceRef?.current && 
                        currentPlayingInstanceRef.current.type === 'target' && 
                        currentPlayingInstanceRef.current.subtitleId === subtitleId &&
                        transAudioRef?.current && 
                        !transAudioRef.current.paused) {
                        transAudioRef.current.playbackRate = roundedSpeed;
                        // 时间轴调节：实时更新全局播放实例音频语速
                    }
                } else {
                    revertTranslatedSegmentFromVideoData();
                }
            } catch (error: any) {
                revertTranslatedSegmentFromVideoData();
                console.error('时间轴调节倍速失败:', error);
                
                // 检查是否是"数据已被修改"错误
                const errorMessage = error?.message || error?.detail || error?.error || '';
                if (errorMessage.includes('数据已被修改') || errorMessage.includes('请刷新后重试')) {
                    console.warn('数据已被修改，建议刷新页面后重试');
                    // 可以在这里添加用户提示，比如使用 showError 或 message
                    // 但这里没有 showError 的上下文，所以只在控制台输出
                } else {
                    // 其他错误也输出到控制台
                    console.error('时间轴调节倍速失败，错误详情:', {
                        message: errorMessage,
                        status: error?.status,
                        response: error?.response
                    });
                }
            } finally {
                // 隐藏加载遮罩
                if (this.setIsAdjustingSpeed) {
                    this.setIsAdjustingSpeed(false);
                }
            }
        }
        // 更新字幕时间戳（拖动字幕时调用，只在鼠标松开时触发）
        async updateSubtitleTimestamps(subtitleId: number, newStartTime: number, newEndTime: number) {
            // 如果已经有请求在进行中，取消之前的请求，使用最新的拖动结果
            if (this.isDragApiCalling && this.dragApiRequestRef) {
                console.log('取消之前的拖动API请求，使用最新的拖动结果');
                // 标记之前的请求为已取消（不等待完成）
                this.isDragApiCalling = false;
                this.dragApiRequestRef = null;
            }
            
            // 标记请求开始
            this.isDragApiCalling = true;
            
            // 创建请求Promise并保存
            const requestPromise = (async () => {
                try {
                    const postStartTime = formatSecondsToTimestamp(newStartTime, '.');
                    const postEndTime = formatSecondsToTimestamp(newEndTime, '.');
                    
                    // 拖动字幕：调用API更新时间戳
                    
                    // 调用新接口：/video/subtitles/adjust-timestamp-range
                    const response = await adjustTimestampRangeApi({
                        id: subtitleId,
                        postStartTime: postStartTime,
                        postEndTime: postEndTime
                    });
                    
                    if (response.code === 0 && response.data) {
                        // 拖动字幕：API调用成功
                        
                        // 将用户传入的postStartTime和postEndTime同步到本地数据
                        // 注意：需要转换为本地使用的格式（逗号分隔毫秒：HH:mm:ss,SSS）
                        const formatTimeForLocal = (timeStr: string): string => {
                            return timeStr.replace('.', ',');
                        };
                        
                        const localPostStartTime = formatTimeForLocal(postStartTime);
                        const localPostEndTime = formatTimeForLocal(postEndTime);
                        
                        // 1. 直接更新右侧台词区域的时间范围
                        this.setTargetText?.(prev => prev.map(item => {
                            if (item.id === subtitleId) {
                                return {
                                    ...item,
                                    timeRange: `${localPostStartTime} → ${localPostEndTime}`
                                };
                            }
                            return item;
                        }));

                        // 2. 立即同步到 videoData.subtitles，避免后续 useEffect 用旧时间戳覆盖拖动结果
                        this.setVideoData?.(prev => {
                            if (!prev?.subtitles) return prev;

                            const updatedSubtitles = prev.subtitles.map(subtitle => {
                                if (subtitle.id === subtitleId) {
                                    return {
                                        ...subtitle,
                                        post_start_time: localPostStartTime,
                                        post_end_time: localPostEndTime
                                    };
                                }
                                return subtitle;
                            });

                            return {
                                ...prev,
                                subtitles: updatedSubtitles
                            };
                        });

                        // 3. 更新/清理待同步队列（只保留当前最新结果，防止脏数据）
                        this.setPendingTimelineUpdates?.(prev => {
                            const newMap = new Map(prev);
                            newMap.set(subtitleId, {
                                audio_speed: prev.get(subtitleId)?.audio_speed || 1.0, // 保持原有的倍速值
                                post_start_time: localPostStartTime,
                                post_end_time: localPostEndTime
                            });
                            return newMap;
                        });
                    }
                } catch (error) {
                    console.error('拖动字幕：API调用失败', error);
                } finally {
                    // 请求完成，清除标记
                    this.isDragApiCalling = false;
                    this.dragApiRequestRef = null;
                }
            })();
            
            // 保存请求引用
            this.dragApiRequestRef = requestPromise;
            
            // 等待请求完成
            await requestPromise;
        }

        /**
         * 纯净版校对：拖动/拉伸「原视频」轨片段，仅更新原文起止时间（不改编译轨、不调语速）
         */
        async updateOriginSubtitleTimestamps(subtitleId: number, newStartTime: number, newEndTime: number) {
            if (this.isDragApiCalling && this.dragApiRequestRef) {
                this.isDragApiCalling = false;
                this.dragApiRequestRef = null;
            }
            this.isDragApiCalling = true;
            const requestPromise = (async () => {
                try {
                    const originStartTime = formatSecondsToTimestamp(newStartTime, ',');
                    const originEndTime = formatSecondsToTimestamp(newEndTime, ',');
                    const response = await adjustOriginTimestampRangeApi({
                        id: subtitleId,
                        originStartTime,
                        originEndTime,
                    });
                    if (response.code === 0 && response.data) {
                        const localA = originStartTime;
                        const localB = originEndTime;
                        this.setTargetText?.(prev =>
                            prev.map((item) =>
                                item.id === subtitleId
                                    ? { ...item, timeRange: `${localA} → ${localB}` }
                                    : item
                            )
                        );
                        this.setVideoData?.((prev) => {
                            if (!prev?.subtitles) return prev;
                            return {
                                ...prev,
                                subtitles: prev.subtitles.map((sub) => {
                                    if (sub.id !== subtitleId) return sub;
                                    return {
                                        ...sub,
                                        origin_start_time: localA,
                                        origin_end_time: localB,
                                    };
                                }),
                            };
                        });
                        this.onOriginTimestampSaved?.(subtitleId, localA, localB);
                    }
                } catch (error) {
                    console.error('校准原文时间戳：API 调用失败', error);
                } finally {
                    this.isDragApiCalling = false;
                    this.dragApiRequestRef = null;
                }
            })();
            this.dragApiRequestRef = requestPromise;
            await requestPromise;
        }
        
        // 更新数据（避免频繁销毁重建）
        updateData(newVideoData: any) {
            this.videoData = newVideoData;
            this.totalTime = this.calculateTotalTime(newVideoData);
            this.syncTimelineWidthCssVars();
            // 重新渲染刻度和片段，但不重新绑定 document 事件
            this.generateTimeMarks();
            if (newVideoData?.subtitles) {
                this.generateSegmentsFromSubtitles(newVideoData.subtitles);
            }
        }
        
        // 销毁方法：清理所有资源和事件监听器
        destroy() {
            console.log('清理 TimelineEditor 资源和监听器');
            
            // 移除拖拽相关
            if (this.dragMouseDownHandler) {
                document.removeEventListener('mousedown', this.dragMouseDownHandler);
            }
            if (this.dragMouseMoveHandler) {
                document.removeEventListener('mousemove', this.dragMouseMoveHandler);
            }
            if (this.dragMouseUpHandler) {
                document.removeEventListener('mouseup', this.dragMouseUpHandler);
            }
            
            // 移除播放头相关
            const playhead = document.getElementById('playhead');
            if (playhead && this.playheadMouseDownHandler) {
                playhead.removeEventListener('mousedown', this.playheadMouseDownHandler);
            }
            if (this.playheadDocumentMouseDownHandler) {
                document.removeEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            }
            if (this.playheadDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            }
            if (this.playheadDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            }
            
            if (this.detachTimelineScrollbar) {
                this.detachTimelineScrollbar();
                this.detachTimelineScrollbar = undefined;
            }

            this.teardownOriginTrackResize();
            
            // 清理定时器
            if (this.playheadAnimationId) {
                cancelAnimationFrame(this.playheadAnimationId);
            }
            
            // 重置所有引用
            this.dragMouseDownHandler = undefined;
            this.dragMouseMoveHandler = undefined;
            this.dragMouseUpHandler = undefined;
            this.playheadMouseDownHandler = undefined;
            this.playheadDocumentMouseDownHandler = undefined;
            this.playheadDocumentMouseMoveHandler = undefined;
            this.playheadDocumentMouseUpHandler = undefined;
        }
        
        // 同步待更新的时间轴数据到videoData
        syncPendingUpdatesToVideoData(pendingUpdates: Map<number, { audio_speed: number; post_start_time: string; post_end_time: string; }>) {
            if (pendingUpdates.size === 0) return;
            
            // 同步待更新的时间轴数据到videoData
            
            this.setVideoData?.(prev => {
                if (!prev?.subtitles) return prev;
                
                const updatedSubtitles = prev.subtitles.map(subtitle => {
                    const pendingUpdate = pendingUpdates.get(subtitle.id);
                    if (pendingUpdate) {
                        return {
                            ...subtitle,
                            audio_speed: pendingUpdate.audio_speed,
                            post_start_time: pendingUpdate.post_start_time,
                            post_end_time: pendingUpdate.post_end_time
                        };
                    }
                    return subtitle;
                });
                
                return {
                    ...prev,
                    subtitles: updatedSubtitles
                };
            });
            
            // 清空待同步队列
            this.setPendingTimelineUpdates?.(new Map());
        }
        
        positionSegments() {
            const segments = document.querySelectorAll('.segment');
            const totalTime = this.totalTime; // 使用动态计算的总时长

            // 批量更新样式，减少重排
            const updates: Array<{ element: HTMLElement; left: string; width: string }> = [];
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const start = parseFloat(element.dataset.start || '0');
                const end = parseFloat(element.dataset.end || '0');
                const duration = end - start;
                
                // 使用缩放后的宽度计算位置
                const leftPercent = (start / totalTime) * 100;
                const widthPercent = (duration / totalTime) * 100;
                
                updates.push({
                    element,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`
                });
            });
            
            // 批量应用样式更新
            requestAnimationFrame(() => {
                updates.forEach(({ element, left, width }) => {
                    element.style.left = left;
                    element.style.width = width;
                });
            });
        }
        
        // 轻量级字幕定位（用于拖动时的实时反馈）
        positionSegmentsLight() {
            const segments = document.querySelectorAll('.segment');
            const totalTime = this.totalTime; // 使用动态计算的总时长
            
            // 批量更新样式，减少重排和重绘
            const updates: Array<{ element: HTMLElement; left: string; width: string }> = [];
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const start = parseFloat(element.dataset.start || '0');
                const end = parseFloat(element.dataset.end || '0');
                const duration = end - start;
                
                // 使用缩放后的宽度计算位置
                const leftPercent = (start / totalTime) * 100;
                const widthPercent = (duration / totalTime) * 100;
                
                updates.push({
                    element,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`
                });
            });
            
            // 批量应用样式更新
            updates.forEach(({ element, left, width }) => {
                element.style.left = left;
                element.style.width = width;
            });
        }
        
        
        setActiveTrack(trackType: string) {
            const prevTrack = this.activeTrack;
            this.activeTrack = trackType;
            
            // 更新轨道标签状态 - 更新为新的左侧面板选择器
            const trackLabels = document.querySelectorAll('.timeline-left-panel .track-label');
            if (trackLabels.length === 1) {
                // 纯净版模式：只有“原视频”一条轨道标签，始终高亮
                trackLabels[0].classList.add('active');
            } else {
                trackLabels.forEach((label, index) => {
                    const isActive = (trackType === 'translated' && index === 0) || 
                                   (trackType === 'original' && index === 1);
                    label.classList.toggle('active', isActive);
                });
            }
            
            // 更新轨道状态
            const translatedTrack = document.getElementById('translatedTrack');
            const originalTrack = document.getElementById('originalTrack');
            
            if (translatedTrack && originalTrack) {
                // 移除所有轨道的active和disabled类
                translatedTrack.classList.remove('active', 'disabled');
                originalTrack.classList.remove('active', 'disabled');
                
                // 设置激活轨道的状态
                if (trackType === 'translated') {
                    translatedTrack.classList.add('active');
                    originalTrack.classList.add('disabled'); // 非激活轨道显示为禁用状态
                } else {
                    originalTrack.classList.add('active');
                    translatedTrack.classList.add('disabled'); // 非激活轨道显示为禁用状态
                }
                
                // 更新字幕状态
                const translatedSegments = translatedTrack.querySelectorAll('.segment');
                const originalSegments = originalTrack.querySelectorAll('.segment');
                
                if (trackType === 'translated') {
                    translatedSegments.forEach(segment => segment.classList.remove('inactive'));
                    originalSegments.forEach(segment => segment.classList.add('inactive'));
                } else {
                    originalSegments.forEach(segment => segment.classList.remove('inactive'));
                    translatedSegments.forEach(segment => segment.classList.add('inactive'));
                }
            }
            
            if (prevTrack !== trackType) {
                document.querySelectorAll('.segment').forEach(s => s.classList.remove('active'));
                this.onSegmentSelectionChange?.(null);
                if (this.segmentPlayingLockLineIdRef) {
                    this.segmentPlayingLockLineIdRef.current = null;
                }
            }
            this.onActiveTrackChange?.(trackType as 'translated' | 'original');
        }
        
        // 检测片段是否与其他片段重叠
        private checkOverlappingSegments(segment: HTMLElement): boolean {
            const track = segment.closest('.track') as HTMLElement;
            if (!track) return false;
            
            const segmentStart = parseFloat(segment.dataset.start || '0');
            const segmentEnd = parseFloat(segment.dataset.end || '0');
            
            // 获取同一轨道内的所有片段
            const allSegmentsInTrack = track.querySelectorAll('.segment');
            
            // 检查是否有其他片段与当前片段重叠
            for (const otherSegment of allSegmentsInTrack) {
                if (otherSegment === segment) continue; // 跳过自己
                
                const otherStart = parseFloat((otherSegment as HTMLElement).dataset.start || '0');
                const otherEnd = parseFloat((otherSegment as HTMLElement).dataset.end || '0');
                
                // 检测时间范围是否重叠：两个时间段有交集
                if (segmentStart < otherEnd && segmentEnd > otherStart) {
                    return true; // 有重叠
                }
            }
            
            return false; // 无重叠
        }
        
        private notifySegmentSelected(segment: HTMLElement, trackType: string) {
            const startTime = parseFloat(segment.dataset.start || '0');
            this.currentTime = startTime;
            // 片段点击先只更新时间轴自身，再由外层事件统一执行一次 seek/播放逻辑，
            // 避免这里和 timeline:segmentSelected 双重触发 onPlayheadMove。
            this.updatePlayheadPosition({ syncVideo: false, scrollBehavior: 'auto' });

            try {
                const subtitleIdRaw = parseInt(segment.dataset.subtitleId || '0', 10);
                window.dispatchEvent(new CustomEvent('timeline:segmentSelected', {
                    detail: {
                        time: startTime,
                        trackType: trackType,
                        subtitleId: Number.isFinite(subtitleIdRaw) && subtitleIdRaw > 0 ? subtitleIdRaw : undefined
                    }
                }));
            } catch (e) {
                // 安全兜底
                console.warn('派发时间轴片段选择事件失败:', e);
            }
        }

        selectSegment(segment: HTMLElement, options?: { notifySelection?: boolean }) {
            const track = segment.closest('.track') as HTMLElement;
            const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
            const shouldNotifySelection = options?.notifySelection !== false;

            const sidRaw = parseInt(segment.dataset.subtitleId || '0', 10);
            const lockLineId = Number.isFinite(sidRaw) && sidRaw > 0 ? sidRaw : null;

            this.setActiveTrack(trackType);

            if (this.segmentPlayingLockLineIdRef) {
                this.segmentPlayingLockLineIdRef.current = lockLineId;
            }
            
            // 移除其他片段的选中状态
            document.querySelectorAll('.segment').forEach(s => s.classList.remove('active'));
            
            // 选中当前片段
            segment.classList.add('active');
            
            // 检测是否有重叠，如果有则将被点击的片段置顶
            if (this.checkOverlappingSegments(segment)) {
                // 增加 z-index 计数器，并将被点击的片段置顶
                this.maxZIndex += 1;
                
                // 防止片段 z-index 把播放头遮挡住：
                // 播放头通过 CSS 固定 z-index=10（见 videoEdit.scss），
                // 这里把片段 z-index 强制限制在播放头之下。
                const playhead = document.getElementById('playhead');
                const playheadZIndex = playhead
                    ? Number.parseInt(window.getComputedStyle(playhead).zIndex || '10', 10)
                    : 10;
                
                const safeMaxZIndex = Number.isFinite(playheadZIndex) ? Math.max(0, playheadZIndex - 1) : 9;
                const cappedZIndex = Math.min(this.maxZIndex, safeMaxZIndex);
                this.maxZIndex = cappedZIndex;
                segment.style.zIndex = cappedZIndex.toString();
                
                // 确保其他重叠的片段保持较低的 z-index（可选，让它们保持原有层级）
                // 这里我们只提升被点击的片段，其他片段保持原样
            }
            
            if (shouldNotifySelection) {
                this.notifySegmentSelected(segment, trackType);
            }

            this.onSegmentSelectionChange?.(lockLineId);
        }
        
        initPlayhead() {
            const playhead = document.getElementById('playhead');
            if (!playhead) return;
            
            let isDraggingPlayhead = false;
            let hasDragMoved = false;
            let dragStartClientX = 0;
            let pendingClickTime: number | null = null;
            
            // 性能优化：缓存DOM元素和计算值
            let cachedTracksContainer: HTMLElement | null = null;
            let containerRect: DOMRect | null = null;
            let lastUpdateTime = 0;
            const UPDATE_THROTTLE = 16; // 约60fps，减少更新频率
            
            // 如果已经绑定过事件，先移除旧的监听器
            if (this.playheadMouseDownHandler) {
                playhead.removeEventListener('mousedown', this.playheadMouseDownHandler);
            }
            if (this.playheadDocumentMouseDownHandler) {
                document.removeEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            }
            if (this.playheadDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            }
            if (this.playheadDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            }
            
            // 播放头拖拽开始
            this.playheadMouseDownHandler = (e: MouseEvent) => {
                if (this.timelineScrubbingRef) {
                    this.timelineScrubbingRef.current = true;
                }
                isDraggingPlayhead = true;
                hasDragMoved = false;
                dragStartClientX = e.clientX;
                pendingClickTime = null;
                e.preventDefault();
                e.stopPropagation();
                // 阻止文字选择
                document.body.style.userSelect = 'none';
                
                // 缓存DOM元素，避免重复查询
                cachedTracksContainer = document.querySelector('.tracks-container') as HTMLElement;
                if (cachedTracksContainer) {
                    containerRect = cachedTracksContainer.getBoundingClientRect();
                }
                // 播放头拖拽开始
            };
            playhead.addEventListener('mousedown', this.playheadMouseDownHandler);
            
            // 时间轴点击和拖拽处理
            this.playheadDocumentMouseDownHandler = (e: MouseEvent) => {
                // 如果点击的是字幕片段，不处理
                if ((e.target as HTMLElement).closest('.segment')) {
                    return;
                }
                
                // 如果点击的是轨道内容区域或时间尺子
                const trackContent = (e.target as HTMLElement).closest('.track-content');
                const timeRuler = (e.target as HTMLElement).closest('.time-ruler');
                const tracksContainer = (e.target as HTMLElement).closest('.tracks-container');
                
                if (trackContent || timeRuler || tracksContainer) {
                    const targetElement = trackContent || timeRuler || tracksContainer;
                    if (!targetElement) return;
                    
                    if (this.timelineScrubbingRef) {
                        this.timelineScrubbingRef.current = true;
                    }
                    // 阻止事件冒泡，确保点击事件处理正确
                    e.stopPropagation();
                    
                    const rect = targetElement.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                    
                    // mousedown 仅记录点击目标时间；统一在 mouseup 提交一次 seek，避免“按下+抬起”双定位。
                    pendingClickTime = percentage * this.totalTime;
                    
                    // 立即开始拖拽跟随（鼠标按下瞬间就开始跟随）
                    isDraggingPlayhead = true;
                    hasDragMoved = false;
                    dragStartClientX = e.clientX;
                    // 阻止文字选择
                    document.body.style.userSelect = 'none';
                    
                    // 缓存DOM元素
                    cachedTracksContainer = document.querySelector('.tracks-container') as HTMLElement;
                    if (cachedTracksContainer) {
                        containerRect = cachedTracksContainer.getBoundingClientRect();
                    }
                    // 时间轴拖拽开始
                }
            };
            document.addEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            
            // 全局鼠标移动事件（优化性能）
            this.playheadDocumentMouseMoveHandler = (e: MouseEvent) => {
                if (isDraggingPlayhead) {
                    e.preventDefault();
                    if (Math.abs(e.clientX - dragStartClientX) > 1) {
                        hasDragMoved = true;
                    }
                    
                    // 节流：限制更新频率，提高性能
                    const now = Date.now();
                    if (now - lastUpdateTime < UPDATE_THROTTLE) {
                        return;
                    }
                    lastUpdateTime = now;
                    
                    if (cachedTracksContainer && containerRect) {
                        const clickX = e.clientX - containerRect.left;
                        const percentage = Math.max(0, Math.min(1, clickX / containerRect.width));
                        
                        // 实时更新播放头位置
                        this.currentTime = percentage * this.totalTime;
                        this.updatePlayheadPositionOptimized();
                        
                        // 拖动播放头时，同步滚动轨道内容
                        const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
                        if (timelineContent) {
                            const containerWidth = cachedTracksContainer.clientWidth;
                            const translateXPx = (this.currentTime / this.totalTime) * containerWidth;
                            
                            // 计算播放头在滚动容器中的绝对位置
                            const scrollWidth = timelineContent.scrollWidth;
                            const clientWidth = timelineContent.clientWidth;
                            const currentScrollLeft = timelineContent.scrollLeft;
                            
                            // 计算播放头相对于可视区域的偏移
                            const playheadRelativePos = translateXPx - currentScrollLeft;
                            
                            // 如果播放头超出可视区域右侧，向右滚动（留50px的缓冲区域）
                            if (playheadRelativePos > clientWidth - 50) {
                                const targetScrollLeft = translateXPx - clientWidth + 50;
                                timelineContent.scrollLeft = Math.min(targetScrollLeft, scrollWidth - clientWidth);
                                // 同步更新滑动条位置（滚动时只更新位置，不重新计算宽度）
                                this.updateScrollbarPosition();
                            }
                            // 如果播放头超出可视区域左侧，向左滚动（留50px的缓冲区域）
                            else if (playheadRelativePos < 50) {
                                const targetScrollLeft = translateXPx - 50;
                                timelineContent.scrollLeft = Math.max(0, targetScrollLeft);
                                // 同步更新滑动条位置（滚动时只更新位置，不重新计算宽度）
                                this.updateScrollbarPosition();
                            }
                        }
                    }
                }
            };
            document.addEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            
            // 全局鼠标释放事件
            this.playheadDocumentMouseUpHandler = () => {
                if (isDraggingPlayhead) {
                    // 纯点击（无拖动）在 mouseup 时应用一次定位，避免按下即定位产生双触发体感。
                    if (!hasDragMoved && pendingClickTime !== null) {
                        this.currentTime = pendingClickTime;
                    }
                    // 时间轴拖拽结束
                    
                    // 拖拽结束时进行完整的同步（确保最终位置准确）
                    if (this.onPlayheadMove) {
                        this.onPlayheadMove(this.currentTime);
                    }
                }
                
                isDraggingPlayhead = false;
                if (this.timelineScrubbingRef) {
                    this.timelineScrubbingRef.current = false;
                }
                
                // 清理缓存
                cachedTracksContainer = null;
                containerRect = null;
                pendingClickTime = null;
                hasDragMoved = false;
                
                // 恢复文字选择功能
                document.body.style.userSelect = '';
            };
            document.addEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            
            // 不再监听 document click：mousedown 已更新预览，mouseup 统一提交一次 seek，
            // 若再监听 click 会与 mousedown/mouseup 叠加，导致重复 seek + smooth 滚动抖动。
        }
        
        /**
         * 更新播放头位置与可选滚动。
         * @param options.syncVideo 为 false 时只更新 DOM，不调用 onPlayheadMove（用于时间轴拖拽/点击过程中的预览）
         * @param options.scrollBehavior 时间轴横向滚动方式；seek 预览建议 auto，避免与多次更新叠加产生抖动
         */
        updatePlayheadPosition(options?: { syncVideo?: boolean; scrollBehavior?: ScrollBehavior }) {
            const syncVideo = options?.syncVideo !== false;
            const scrollBehavior: ScrollBehavior = options?.scrollBehavior ?? 'smooth';
            const playhead = document.getElementById('playhead');
            if (!playhead) return;

            const container = document.querySelector('.tracks-container') as HTMLElement | null;
            const containerWidth = container?.clientWidth || playhead.parentElement?.clientWidth || 0;
            if (containerWidth <= 0 || this.totalTime <= 0) return;

            const translateXPx = (this.currentTime / this.totalTime) * containerWidth;
            const transformValue = `translateX(${translateXPx.toFixed(2)}px)`;

            if (playhead.style.transform !== transformValue) {
                playhead.style.transform = transformValue;
            }

            // 自动滚动：让播放头保持在可视区域内
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (timelineContent) {
                const scrollLeft = timelineContent.scrollLeft;
                const scrollWidth = timelineContent.scrollWidth;
                const clientWidth = timelineContent.clientWidth;
                
                // 计算播放头在滚动容器中的绝对位置（translateXPx 就是相对于 tracks-container 的位置，也就是在 timeline-content 中的绝对位置）
                const playheadAbsolutePos = translateXPx;
                
                // 计算播放头相对于可视区域的偏移
                const playheadRelativePos = playheadAbsolutePos - scrollLeft;
                
                // 如果播放头超出可视区域右侧，向右滚动（留50px的缓冲区域）
                if (playheadRelativePos > clientWidth - 50) {
                    const targetScrollLeft = Math.min(playheadAbsolutePos - clientWidth + 50, scrollWidth - clientWidth);
                    timelineContent.scrollTo({
                        left: targetScrollLeft,
                        behavior: scrollBehavior
                    });
                }
                // 如果播放头超出可视区域左侧，向左滚动（留50px的缓冲区域）
                else if (playheadRelativePos < 50) {
                    const targetScrollLeft = Math.max(0, playheadAbsolutePos - 50);
                    timelineContent.scrollTo({
                        left: targetScrollLeft,
                        behavior: scrollBehavior
                    });
                }
                
                // 注意：滚动条位置会通过滚动事件监听器自动同步更新，无需手动调用
                // 但为了确保初始状态正确，仍然调用一次（滚动事件会在滚动过程中持续更新）
                this.updateScrollbar();
            } else {
                // 如果没有 timelineContent，也要尝试更新滚动条（可能DOM还没准备好）
                this.updateScrollbar();
            }

            // 高亮当前播放时间对应的片段
            this.highlightCurrentSegments();

            // 同步视频播放进度（通过回调函数）
            if (syncVideo && this.onPlayheadMove) {
                this.onPlayheadMove(this.currentTime);
            }
        }
        
        // 优化的播放头位置更新（拖拽时使用，只更新位置，不进行其他操作）
        updatePlayheadPositionOptimized() {
            const playhead = document.getElementById('playhead');
            if (!playhead) return;

            const container = document.querySelector('.tracks-container') as HTMLElement | null;
            const containerWidth = container?.clientWidth || playhead.parentElement?.clientWidth || 0;
            if (containerWidth <= 0 || this.totalTime <= 0) return;

            // 1. 直接使用视频当前时间计算目标位置
            const videoTime = this.currentTime;
            const targetPosition = (videoTime / this.totalTime) * containerWidth;

            // 2. 直接更新播放头位置（拖动时不需要缓动动画，需要立即响应）
            playhead.style.transform = `translateX(${targetPosition.toFixed(2)}px)`;

            // 拖拽过程中不进行片段高亮和视频同步，只在拖拽结束时进行
        }
        
        // 高亮当前播放时间对应的片段（优化版本）
        highlightCurrentSegments() {
            const segments = document.querySelectorAll('.segment');
            const lockId = this.segmentPlayingLockLineIdRef?.current ?? null;

            const pickPlayingRoots = (): Set<HTMLElement> => {
                const roots = new Set<HTMLElement>();
                if (lockId != null && lockId > 0) {
                    segments.forEach(segment => {
                        const el = segment as HTMLElement;
                        const sid = parseInt(el.dataset.subtitleId || '0', 10);
                        if (sid === lockId) roots.add(el);
                    });
                    return roots;
                }
                const bestEl = new Map<HTMLElement, HTMLElement>();
                const bestStart = new Map<HTMLElement, number>();
                segments.forEach(segment => {
                    const el = segment as HTMLElement;
                    const track = el.closest('.track') as HTMLElement | null;
                    if (!track) return;
                    const start = parseFloat(el.dataset.start || '0');
                    const end = parseFloat(el.dataset.end || '0');
                    if (this.currentTime < start || this.currentTime > end) return;
                    const prev = bestStart.get(track);
                    if (prev === undefined || start > prev) {
                        bestStart.set(track, start);
                        bestEl.set(track, el);
                    }
                });
                bestEl.forEach(el => roots.add(el));
                return roots;
            };

            const playing = pickPlayingRoots();
            segments.forEach(segment => {
                const el = segment as HTMLElement;
                const on = playing.has(el);
                const was = el.classList.contains('playing');
                if (on && !was) el.classList.add('playing');
                else if (!on && was) el.classList.remove('playing');
            });
        }
        
        initScrollbar() {
            const el = getTimelineScrollbarElements();
            if (!el) return;

            this.detachTimelineScrollbar?.();
            this.detachTimelineScrollbar = attachTimelineScrollbarInteractions(el, () => {
                this.updateScrollbarPosition();
            });
            this.updateScrollbar();
        }
        
        updateScrollbar() {
            const el = getTimelineScrollbarElements();
            if (!el) return;
            updateTimelineScrollbarFull(el);
        }
        
        // 仅更新滚动条位置（不重新计算宽度），用于滚动时的实时更新
        updateScrollbarPosition() {
            const el = getTimelineScrollbarElements();
            if (!el) return;
            updateTimelineScrollbarPositionOnly(el);
        }
        
        
        initTrackClick() {
            // 轨道点击事件处理
        }
        
        // 初始化缩放控制（现在通过React状态管理，不需要DOM事件监听）
        initZoomControls() {
            // 缩放控制现在通过React状态管理，不需要直接操作DOM
            // 这个方法保留用于其他可能的初始化逻辑
        }
        
        /** 将总时长与缩放系数写入 .timeline-content，由 CSS 驱动 .time-ruler / .tracks-container 宽度 */
        syncTimelineWidthCssVars() {
            const el = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement | null;
            if (!el) return;
            const scale = calcTimelineZoomScale(this.zoomLevel);
            el.style.setProperty('--timeline-zoom-scale', String(scale));
            el.style.setProperty('--timeline-total-sec', String(this.totalTime));
        }

        // 更新缩放
        updateZoom(value: number) {
            this.zoomLevel = value;
            this.applyZoom('full');
            // 通知React组件更新状态
            if (this.onZoomChange) {
                this.onZoomChange(value);
            }
        }
        
        // 轻量级缩放更新（只更新宽度，不重新生成时间标记）
        updateZoomLight(value: number) {
            this.zoomLevel = value;
            this.applyZoom('light');
        }
        
        // 完整缩放更新（包含时间标记重新生成）
        updateZoomComplete(value: number) {
            this.zoomLevel = value;
            this.applyZoom('complete');
        }
        
        // 放大
        zoomIn() {
            const newValue = Math.min(100, this.zoomLevel + 10);
            this.updateZoom(newValue);
        }
        
        // 缩小
        zoomOut() {
            const newValue = Math.max(0, this.zoomLevel - 10);
            this.updateZoom(newValue);
        }
        
        /**
         * 统一缩放入口（原 applyZoom / applyZoomLight / applyZoomComplete）
         * 宽度由 .timeline-content 上的 CSS 变量驱动，不再对 time-ruler / tracks-container 逐节点写 minWidth
         */
        applyZoom(mode: 'full' | 'light' | 'complete' = 'full') {
            this.syncTimelineWidthCssVars();
            const scale = calcTimelineZoomScale(this.zoomLevel);

            if (mode === 'light') {
                const startTime = performance.now();
                requestAnimationFrame(() => {
                    this.positionSegmentsLight();
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                        const endTime = performance.now();
                        if (endTime - startTime > 16) {
                            console.warn(`缩放操作耗时过长: ${(endTime - startTime).toFixed(2)}ms`);
                        }
                    });
                });
                return;
            }

            if (mode === 'complete') {
                requestAnimationFrame(() => {
                    this.generateTimeMarks();
                    this.lastScale = scale;
                    this.positionSegments();
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                    });
                });
                return;
            }

            requestAnimationFrame(() => {
                const scaleChange = Math.abs(scale - (this.lastScale || 1));
                if (scaleChange > 0.1) {
                    this.generateTimeMarks();
                    this.lastScale = scale;
                }
                this.positionSegments();
                requestAnimationFrame(() => {
                    this.updateScrollbar();
                });
            });
        }
        
        // 将时间轴滚动到指定时间位置
        scrollToTime(timeInSeconds: number) {
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (!timelineContent) return;
            
            // 计算目标时间在时间轴上的位置百分比
            const timePercentage = timeInSeconds / this.totalTime;
            
            // 计算需要滚动的距离
            const maxScroll = timelineContent.scrollWidth - timelineContent.clientWidth;
            const targetScrollLeft = timePercentage * maxScroll;
            
            // 立即滚动到目标位置（避免平滑滚动带来的延迟导致音画不同步）
            timelineContent.scrollTo({
                left: targetScrollLeft,
                behavior: 'auto'
            });
            
            // 同时更新滚动条位置
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track') as HTMLElement;
            
            if (scrollbarThumb && scrollbarTrack) {
                const trackWidth = scrollbarTrack.offsetWidth;
                const thumbWidth = scrollbarThumb.offsetWidth;
                const thumbPosition = timePercentage * (trackWidth - thumbWidth);
                
                scrollbarThumb.style.left = `${thumbPosition}px`;
            }
            
            // 时间轴滚动到指定时间
        }

        // 将时间轴滚动到使指定台词片段水平居中的位置（用于上下键/点击台词切换时）
        scrollToSegmentCenter(subtitleId: number) {
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (!timelineContent) return;

            const translatedTrack = document.getElementById('translatedTrack');
            const originalTrack = document.getElementById('originalTrack');
            const track =
                this.activeTrack === 'translated' && translatedTrack
                    ? translatedTrack
                    : originalTrack;
            if (!track) return;

            const segment = track.querySelector(`[data-subtitle-id="${subtitleId}"]`) as HTMLElement;
            if (!segment) return;

            const containerRect = timelineContent.getBoundingClientRect();
            const segmentRect = segment.getBoundingClientRect();
            const segmentLeftInContent = segmentRect.left - containerRect.left + timelineContent.scrollLeft;
            const segmentWidth = segmentRect.width;
            const containerWidth = timelineContent.clientWidth;
            const maxScroll = Math.max(0, timelineContent.scrollWidth - containerWidth);

            let targetScrollLeft = segmentLeftInContent - containerWidth / 2 + segmentWidth / 2;
            targetScrollLeft = Math.max(0, Math.min(maxScroll, targetScrollLeft));

            timelineContent.scrollLeft = targetScrollLeft;
            this.updateScrollbarPosition();
        }
    }

        useEffect(() => {
        if (videoData?.id && !isLoadingVideo) {
            // 如果已存在旧实例，先执行销毁
            if (timelineEditorRef.current) {
                timelineEditorRef.current.destroy();
            }
            
            // 延迟初始化，确保DOM已渲染
            const timer = setTimeout(() => {
                timelineEditorRef.current = new TimelineEditor(
                    handleZoomChange,
                    onPlayheadMove,
                    videoData,
                    setIsAdjustingSpeed,
                    timelineScrubbingRef,
                    onTimelineSegmentSelectionChange,
                    onTimelineActiveTrackChange,
                    setVideoData,
                    setTargetText,
                    setPendingTimelineUpdates,
                    isFromPure,
                    onOriginTimestampSaved,
                    segmentPlayingLockLineIdRef
                );
                
                // 如果有字幕数据，初始化字幕片段
                if (videoData.subtitles && videoData.subtitles.length > 0) {
                    timelineEditorRef.current.initSubtitles(videoData.subtitles);
                }
                
                // 延迟更新滚动条，确保DOM完全渲染和缩放应用完成
                setTimeout(() => {
                    timelineEditorRef.current?.updateScrollbar();
                }, 200);
            }, 100);

            return () => {
                clearTimeout(timer);
                // 清理防抖定时器
                if (zoomDebounceRef.current) {
                    clearTimeout(zoomDebounceRef.current);
                }
                // 清理节流定时器
                if (zoomThrottleRef.current) {
                    clearTimeout(zoomThrottleRef.current);
                }
                // 【关键】卸载或重新初始化前清理
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.destroy();
                }
            };
        }
    }, [videoData?.id, isLoadingVideo, isFromPure, onOriginTimestampSaved]); // 纯净/回调变化时重建，内容通过 updateData 同步

        useEffect(() => {
        if (timelineEditorRef.current && videoData && !isLoadingVideo && videoData.id) {
            // 只有当TimelineEditor已经初始化后才更新数据
            timelineEditorRef.current.updateData(videoData);
        }
    }, [videoData, isLoadingVideo]);

        // 预处理字幕数据：创建targetText的Map以便快速查找，并预处理时间戳
    // 使用useMemo确保与targetText同步更新，避免数据不同步问题
    const processedSubtitlesData = useMemo(
        () => preprocessSubtitlesByTargetTime(videoData?.subtitles, targetText),
        [videoData?.subtitles, targetText]
    );

        // 使用ref存储预处理数据，便于在playTransAudioByTime中快速访问
    const processedSubtitlesRef = useRef(processedSubtitlesData.processed);
    const targetTextMapRef = useRef(processedSubtitlesData.targetMap);

    // 同步更新ref中的数据（确保ref始终使用最新的数据）
    useEffect(() => {
        processedSubtitlesRef.current = processedSubtitlesData.processed;
        targetTextMapRef.current = processedSubtitlesData.targetMap;
    }, [processedSubtitlesData]);

        const translatedTimelineModels = useMemo(
        () =>
            processedSubtitlesData.processed.map(p => ({
                id: p.id,
                subtitle: p.subtitle,
                startTime: p.startTime,
                endTime: p.endTime
            })),
        [processedSubtitlesData.processed]
    );

        const getTimelineTotalTimeSec = useCallback(() => {
        const te = timelineEditorRef.current;
        const fromEditor = te && te.totalTime > 0 ? te.totalTime : 0;
        return fromEditor > 0 ? fromEditor : Math.max(20, duration || 0);
    }, [duration]);

        const translatedTrackSegments = useMemo(() => {
        return processedSubtitlesData.processed
            .filter(item => !!item.subtitle.trans_audio)
            .map(item => ({
                id: item.id,
                url: processTransAudioUrl(item.subtitle.trans_audio),
                startTime: item.startTime,
                endTime: item.endTime
            }))
            .filter(item => !!item.url && item.endTime > item.startTime);
    }, [processedSubtitlesData]);

    const translatedTrackSegmentsRef = useRef(translatedTrackSegments);
    const translatedTrackBuildSignature = useMemo(() => {
        return translatedTrackSegments
            .map(item => `${item.id}|${item.url}|${item.startTime.toFixed(3)}|${item.endTime.toFixed(3)}`)
            .join('||');
    }, [translatedTrackSegments]);

    useEffect(() => {
        translatedTrackSegmentsRef.current = translatedTrackSegments;
    }, [translatedTrackSegments]);

        // 监听字幕数据变化，更新时间轴片段
    useEffect(() => {
        if (timelineEditorRef.current && videoData?.subtitles && videoData.subtitles.length > 0) {
            console.log('字幕数据变化，更新时间轴片段:', videoData.subtitles.length, '个字幕');
            timelineEditorRef.current.initSubtitles(videoData.subtitles);
        }
    }, [videoData?.subtitles]);

    return {
        // State
        zoomLevel,
        setZoomLevel,
        isAdjustingSpeed,
        setIsAdjustingSpeed,
        pendingTimelineUpdates,
        setPendingTimelineUpdates,
        timelineSelectedSubtitleId,
        setTimelineSelectedSubtitleId,
        timelineActiveTrack,
        setTimelineActiveTrack,
        // Refs
        timelineEditorRef,
        timelineScrubbingRef,
        currentTimeRef,
        playheadTotalTimeRef,
        processedSubtitlesRef,
        targetTextMapRef,
        // Functions
        setPlaybackUiTime,
        handleZoomChange,
        handleZoomAfterChange,
        handleZoomIn,
        handleZoomOut,
        onTimelineSegmentSelectionChange,
        onTimelineActiveTrackChange,
        getTimelineTotalTimeSec,
        // Data
        processedSubtitlesData,
        translatedTimelineModels,
        translatedTrackSegments,
        translatedTrackSegmentsRef,
        translatedTrackBuildSignature,
    };
};
